import { createFileRoute, Link } from "@tanstack/react-router";
import { FileText, LockKeyhole, Mail, ShieldCheck } from "lucide-react";

import { SUPPORT_EMAIL } from "@/lib/contact";
import { createWebPageJsonLd, seo } from "@/lib/seo";

const UPDATED_AT = "May 20, 2026";

const privacySections = [
	{
		title: "Information we collect",
		body: [
			"We collect account details such as your name, email address, authentication settings, workspace membership, and plan information.",
			"We collect the content you choose to provide, including prompts, uploaded learning materials, generated study sessions, notes, answers, audio, and related files.",
			"We collect technical and usage information such as device and browser details, IP address, pages visited, feature interactions, diagnostics, and approximate location derived from network information.",
			"Payment details are processed by our payment providers. We receive limited billing records such as plan, subscription status, invoices, and payment events.",
		],
	},
	{
		title: "How we use information",
		body: [
			"We use information to provide StudyOS, create and save interactive learning experiences, personalize study flows, operate accounts, process billing, and respond to support requests.",
			"We use usage and diagnostic information to maintain reliability, improve product quality, prevent abuse, protect accounts, and understand how StudyOS is used.",
			"We may use aggregated or de-identified information for analytics, research, and product development.",
		],
	},
	{
		title: "AI processing and learning content",
		body: [
			"StudyOS may send prompts, files, generated content, and related context to AI and infrastructure providers so the service can generate lessons, quizzes, flashcards, summaries, speech, images, or other study outputs.",
			"Please do not upload content unless you have the right to use it. Avoid adding sensitive personal information unless it is necessary for your learning task.",
			"Generated outputs can be inaccurate. Review important study, professional, medical, legal, or financial information before relying on it.",
		],
	},
	{
		title: "How we share information",
		body: [
			"We share information with service providers that help us host, secure, analyze, support, bill, and operate StudyOS.",
			"If you use StudyOS through an organization, school, or team, account administrators may be able to access workspace, membership, usage, and learning activity information associated with that organization.",
			"We may disclose information when required by law, to protect rights and safety, to investigate abuse, or as part of a merger, acquisition, financing, or sale of assets.",
			"We do not sell your personal information.",
		],
	},
	{
		title: "Your choices",
		body: [
			"You can update certain account information in StudyOS settings.",
			"You can request access, correction, export, or deletion of personal information by contacting us.",
			"You can unsubscribe from non-essential emails through the instructions in those emails. We may still send service, security, billing, and account messages.",
			"Browser settings may let you limit cookies or similar technologies, though some StudyOS features may not work properly without them.",
		],
	},
	{
		title: "Security and retention",
		body: [
			"We use administrative, technical, and organizational safeguards designed to protect personal information.",
			"No online service is perfectly secure, so we cannot guarantee absolute security.",
			"We keep information for as long as needed to provide StudyOS, comply with legal obligations, resolve disputes, enforce agreements, prevent abuse, and maintain backups. When information is no longer needed, we delete it or de-identify it where practical.",
		],
	},
	{
		title: "Children and student use",
		body: [
			"StudyOS is not directed to children under 13. If you believe a child provided personal information without appropriate consent, contact us and we will take appropriate steps.",
			"Schools, parents, guardians, and organizations are responsible for obtaining any required permissions before inviting learners to use StudyOS.",
		],
	},
	{
		title: "International transfers",
		body: [
			"StudyOS and our providers may process information in countries other than where you live. Those countries may have different data protection laws.",
		],
	},
	{
		title: "Changes to this policy",
		body: [
			"We may update this Privacy Policy from time to time. If changes are material, we will take reasonable steps to notify users, such as by updating this page, sending an email, or showing an in-product notice.",
		],
	},
];

export const Route = createFileRoute("/(landing)/privacy/")({
	component: PrivacyPage,
	head: ({ match }) => {
		const title = "Privacy Policy | StudyOS";
		const description =
			"Learn how StudyOS collects, uses, shares, and protects information when you use our AI learning platform.";

		return seo({
			title,
			description,
			pathname: match.pathname,
			keywords: ["StudyOS privacy", "privacy policy", "AI learning privacy"],
			structuredData: createWebPageJsonLd({
				title,
				description,
				pathname: match.pathname,
			}),
		});
	},
});

function PrivacyPage() {
	return (
		<div className="min-h-screen bg-background text-foreground">
			<section className="border-b border-border/70 px-4 pt-20 pb-12 md:pt-28 md:pb-16">
				<div className="mx-auto max-w-4xl">
					<div className="mb-6 inline-flex items-center gap-2 rounded-full border border-border bg-muted px-4 py-1.5 text-sm font-medium text-muted-foreground">
						<ShieldCheck className="size-4 text-primary" />
						Privacy Policy
					</div>
					<h1 className="max-w-3xl text-4xl font-bold tracking-tight text-foreground md:text-5xl">
						How StudyOS handles your information
					</h1>
					<p className="mt-6 max-w-2xl text-lg leading-8 text-muted-foreground">
						This Privacy Policy explains what we collect, why we collect it, how we
						share it, and the choices you have when using StudyOS.
					</p>
					<p className="mt-4 text-sm font-medium text-muted-foreground">
						Last updated: {UPDATED_AT}
					</p>
				</div>
			</section>

			<section className="px-4 py-12 md:py-16">
				<div className="mx-auto grid max-w-4xl gap-8 lg:grid-cols-[220px_minmax(0,1fr)]">
					<aside className="hidden lg:block">
						<div className="sticky top-28 rounded-lg border border-border bg-background p-4">
							<div className="mb-3 flex items-center gap-2 text-sm font-semibold text-foreground">
								<FileText className="size-4 text-primary" />
								Contents
							</div>
							<nav className="space-y-2 text-sm text-muted-foreground">
								{privacySections.map((section) => (
									<a
										key={section.title}
										href={`#${toSectionId(section.title)}`}
										className="block rounded-md px-2 py-1.5 transition-colors hover:bg-muted hover:text-foreground"
									>
										{section.title}
									</a>
								))}
							</nav>
						</div>
					</aside>

					<article className="space-y-10">
						<section className="rounded-lg border border-border bg-background p-6 md:p-8">
							<div className="mb-4 inline-flex rounded-lg bg-primary/10 p-3">
								<LockKeyhole className="size-5 text-primary" />
							</div>
							<h2 className="text-2xl font-semibold tracking-tight text-foreground">
								Overview
							</h2>
							<p className="mt-4 leading-8 text-muted-foreground">
								StudyOS helps people turn source material and prompts into
								interactive learning experiences. To do that, we process information
								you provide, information created while you use the product, and
								information needed to keep the service secure and reliable.
							</p>
						</section>

						{privacySections.map((section) => (
							<section
								key={section.title}
								id={toSectionId(section.title)}
								className="scroll-mt-28"
							>
								<h2 className="text-2xl font-semibold tracking-tight text-foreground">
									{section.title}
								</h2>
								<div className="mt-4 space-y-4">
									{section.body.map((paragraph) => (
										<p
											key={paragraph}
											className="leading-8 text-muted-foreground"
										>
											{paragraph}
										</p>
									))}
								</div>
							</section>
						))}

						<section className="rounded-lg border border-border bg-muted/40 p-6 md:p-8">
							<div className="mb-4 inline-flex rounded-lg bg-background p-3">
								<Mail className="size-5 text-primary" />
							</div>
							<h2 className="text-2xl font-semibold tracking-tight text-foreground">
								Contact us
							</h2>
							<p className="mt-4 leading-8 text-muted-foreground">
								Questions about this policy or your personal information can be sent
								to{" "}
								<a
									href={`mailto:${SUPPORT_EMAIL}`}
									className="font-semibold text-primary hover:underline"
								>
									{SUPPORT_EMAIL}
								</a>
								.
							</p>
						</section>

						<div className="flex flex-wrap gap-3 border-t border-border pt-8">
							<Link
								to="/"
								className="inline-flex items-center rounded-full bg-primary px-5 py-2.5 text-sm font-semibold text-primary-foreground transition-opacity hover:opacity-90"
							>
								Back to home
							</Link>
							<Link
								to="/contact"
								className="inline-flex items-center rounded-full border border-border px-5 py-2.5 text-sm font-semibold text-foreground transition-colors hover:bg-muted"
							>
								Contact StudyOS
							</Link>
						</div>
					</article>
				</div>
			</section>
		</div>
	);
}

function toSectionId(title: string) {
	return title.toLowerCase().replaceAll(" ", "-");
}
